import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import LinkIcon from "@mui/icons-material/Link";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import { useTranslation } from "react-i18next";
import AgentDocumentsSection from "./AgentDocumentsSection";
import AgentKnowledgeExtractDialog, {
  knowledgeQualityChipColor,
  knowledgeQualityLabel,
} from "./AgentKnowledgeExtractDialog";
import { previewAgentKnowledge } from "../../api/client";
import { translateSkillForm } from "../../utils/skillLifecycle";

export default function AgentKnowledgePanel({
  engineType,
  primarySkillKey,
  documents,
  fileInputRef,
  handleClickAddDocs,
  handleDropDocs,
  handleDragOverDocs,
  moveDocument,
  removeDocument,
  readOnly,
  acceptDocs,
  sourceUrls,
  newUrl,
  setNewUrl,
  handleAddUrl,
  handleRemoveUrl,
  knowledgePreviews = {},
  onKnowledgePreviewUpdate,
}) {
  const { t } = useTranslation("settings");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [dialogPreview, setDialogPreview] = useState(null);
  const [dialogTitle, setDialogTitle] = useState("");
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState(null);

  const introKey =
    engineType === "n8n"
      ? "agentsListPage.formKnowledgeIntroN8n"
      : primarySkillKey === "ticket_seller"
        ? translateSkillForm(t, primarySkillKey, "formSectionKnowledgeHint", "agentsListPage.formKnowledgeIntroTicket")
        : primarySkillKey === "faq"
          ? translateSkillForm(t, primarySkillKey, "formSectionKnowledgeHint", "agentsListPage.knowledgeSectionFaqIntro")
          : primarySkillKey === "sdr"
            ? translateSkillForm(t, primarySkillKey, "formSectionKnowledgeHint", "agentsListPage.knowledgeSectionFaqIntro")
            : primarySkillKey === "social_media"
              ? translateSkillForm(t, primarySkillKey, "formSectionKnowledgeHint", "agentsListPage.formSectionKnowledgeHint")
              : "agentsListPage.knowledgeSectionFaqIntro";

  const introText = introKey.startsWith("agentsListPage.") ? t(introKey) : introKey;

  const storePreview = useCallback(
    (key, preview) => {
      if (typeof onKnowledgePreviewUpdate === "function") {
        onKnowledgePreviewUpdate(key, preview);
      }
    },
    [onKnowledgePreviewUpdate]
  );

  const openPreview = useCallback(
    async ({ key, title, payload }) => {
      setDialogTitle(title);
      setDialogOpen(true);
      setDialogLoading(true);
      setDialogError(null);
      setDialogPreview(null);
      try {
        const preview = await previewAgentKnowledge(payload);
        setDialogPreview(preview);
        if (key) storePreview(key, preview);
      } catch (err) {
        setDialogError(err?.message || t("agentsListPage.knowledgePreviewError"));
      } finally {
        setDialogLoading(false);
      }
    },
    [storePreview, t]
  );

  const handlePreviewDocument = useCallback(
    (doc, index) => {
      const url = doc?.url || doc?.file_url;
      if (!url) {
        setDialogTitle(doc?.name || t("agentsListPage.knowledgePreviewTitle"));
        setDialogOpen(true);
        setDialogLoading(false);
        setDialogPreview(null);
        setDialogError(t("agentsListPage.knowledgePreviewNeedUpload"));
        return;
      }
      openPreview({
        key: `doc:${url}`,
        title: doc?.name || url,
        payload: {
          kind: "document",
          file_url: url,
          file_name: doc?.name || "",
        },
      });
    },
    [openPreview, t]
  );

  const handlePreviewUrl = useCallback(
    (url) => {
      openPreview({
        key: `url:${url}`,
        title: url,
        payload: { kind: "url", url },
      });
    },
    [openPreview]
  );

  const scannableSources = useMemo(() => {
    const sources = [];
    (documents || []).forEach((d) => {
      const url = d?.url || d?.file_url;
      if (!url) return;
      sources.push({
        key: `doc:${url}`,
        kind: "document",
        file_url: url,
        file_name: d?.name || "",
      });
    });
    (sourceUrls || []).forEach((u) => {
      if (!u) return;
      sources.push({ key: `url:${u}`, kind: "url", url: u });
    });
    return sources;
  }, [documents, sourceUrls]);

  const handleScanAll = async () => {
    if (!scannableSources.length) {
      setScanError(t("agentsListPage.knowledgeScanEmpty"));
      return;
    }
    setScanLoading(true);
    setScanError(null);
    try {
      const result = await previewAgentKnowledge({
        sources: scannableSources.map(({ key, ...rest }) => rest),
      });
      const items = Array.isArray(result?.items) ? result.items : [];
      items.forEach((item, idx) => {
        const key = scannableSources[idx]?.key;
        if (key) storePreview(key, item);
      });
    } catch (err) {
      setScanError(err?.message || t("agentsListPage.knowledgePreviewError"));
    } finally {
      setScanLoading(false);
    }
  };

  const scanSummary = useMemo(() => {
    const keys = scannableSources.map((s) => s.key);
    let ok = 0;
    let low = 0;
    let empty = 0;
    keys.forEach((k) => {
      const p = knowledgePreviews?.[k];
      if (!p) return;
      if (p.quality === "ok") ok += 1;
      else if (p.quality === "low") low += 1;
      else empty += 1;
    });
    const checked = ok + low + empty;
    return { ok, low, empty, checked, total: keys.length };
  }, [knowledgePreviews, scannableSources]);

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <Typography variant="body2" color="text.secondary">
        {introText}
      </Typography>
      {engineType === "n8n" && (
        <Alert severity="warning" sx={{ py: 0.75 }}>
          {t("agentsListPage.formKnowledgeN8nWarning")}
        </Alert>
      )}
      {primarySkillKey === "faq" && scanSummary.empty > 0 && (
        <Alert severity="warning" sx={{ py: 0.75 }}>
          {t("agentsListPage.faqKnowledgeQualityWarning")}
        </Alert>
      )}
      {primarySkillKey === "faq" && scannableSources.length === 0 && (
        <Alert severity="info" sx={{ py: 0.75 }}>
          {t("agentsListPage.faqKnowledgeRequiredToActivate")}
        </Alert>
      )}

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button
          size="small"
          variant="outlined"
          startIcon={scanLoading ? <CircularProgress size={14} /> : <FactCheckOutlinedIcon />}
          onClick={handleScanAll}
          disabled={readOnly || scanLoading || !scannableSources.length}
        >
          {t("agentsListPage.knowledgeScanButton")}
        </Button>
        {scanSummary.checked > 0 && (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Chip size="small" color="success" label={`${scanSummary.ok} ${t("agentsListPage.knowledgePreviewQualityOk")}`} />
            {scanSummary.low > 0 && (
              <Chip size="small" color="warning" label={`${scanSummary.low} ${t("agentsListPage.knowledgePreviewQualityLow")}`} />
            )}
            {scanSummary.empty > 0 && (
              <Chip size="small" color="error" label={`${scanSummary.empty} ${t("agentsListPage.knowledgePreviewQualityEmpty")}`} />
            )}
          </Stack>
        )}
      </Stack>
      {scanError && (
        <Alert severity="error" onClose={() => setScanError(null)} sx={{ py: 0.5 }}>
          {scanError}
        </Alert>
      )}
      <Typography variant="caption" color="text.secondary">
        {t("agentsListPage.knowledgeScanHint")}
      </Typography>

      <AgentDocumentsSection
        documents={documents}
        fileInputRef={fileInputRef}
        handleClickAdd={handleClickAddDocs}
        handleDrop={handleDropDocs}
        handleDragOver={handleDragOverDocs}
        moveDocument={moveDocument}
        removeDocument={removeDocument}
        readOnly={readOnly}
        accept={acceptDocs}
        embedded
        knowledgePreviews={knowledgePreviews}
        onPreviewDocument={handlePreviewDocument}
      />
      <Box>
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
        >
          <LinkIcon fontSize="small" />
          {t("agentsListPage.formKnowledgeUrlsTitle")}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          <TextField
            size="small"
            placeholder="https://..."
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddUrl())}
            disabled={readOnly}
            sx={{ minWidth: 260, flex: 1 }}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={handleAddUrl}
            disabled={readOnly}
          >
            {t("agentsListPage.formKnowledgeAddUrl")}
          </Button>
        </Stack>
        {sourceUrls.length > 0 && (
          <List dense disablePadding sx={{ bgcolor: "action.hover", borderRadius: 1 }}>
            {sourceUrls.map((url, index) => {
              const preview = knowledgePreviews?.[`url:${url}`];
              return (
                <ListItem key={`${url}-${index}`}>
                  <ListItemText
                    primary={url}
                    secondary={
                      preview ? (
                        <Chip
                          size="small"
                          sx={{ mt: 0.5 }}
                          color={knowledgeQualityChipColor(preview.quality)}
                          label={knowledgeQualityLabel(t, preview.quality)}
                        />
                      ) : null
                    }
                    primaryTypographyProps={{ variant: "body2", noWrap: true }}
                    secondaryTypographyProps={{ component: "div" }}
                  />
                  <ListItemSecondaryAction>
                    <Stack direction="row" spacing={0.25} alignItems="center">
                      <Tooltip title={t("agentsListPage.knowledgePreviewAction")}>
                        <IconButton size="small" edge="end" onClick={() => handlePreviewUrl(url)} aria-label="preview-url">
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!readOnly && (
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={() => handleRemoveUrl(index)}
                          aria-label={t("agentsListPage.formKnowledgeRemoveUrl")}
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>

      <AgentKnowledgeExtractDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        loading={dialogLoading}
        error={dialogError}
        preview={dialogPreview}
        title={dialogTitle}
      />
    </Stack>
  );
}
